// Blog API Service for Bus Ticket System
import { API_BASE_URL } from '../config/api';

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  // Add auth token if available
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  try {
    const response = await fetch(url, config);
    // Handle 401 Unauthorized
    if (response.status === 401) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error(`API request failed: ${error.message}`);
    throw error;
  }
};

// Blog API
const blogAPI = {
  // Get all blog posts (with pagination and filtering)
  getAllBlogPosts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      return await apiRequest(`/blogs?${queryParams}`);
    } catch (error) {
      console.error('Error fetching all blog posts:', error);
      throw error;
    }
  },

  // Get published blog posts for frontend
  getPublishedBlogPosts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      return await apiRequest(`/blogs/published/posts?${queryParams}`);
    } catch (error) {
      console.error('Error fetching published blog posts:', error);
      throw error;
    }
  },

  // Get blog post by ID
  getBlogPostById: async (id) => {
    try {
      const url = `${API_BASE_URL}/blogs/${id}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching blog post with ID ${id}:`, error);
      throw error;
    }
  },

  // Get blog post by slug
  getBlogPostBySlug: async (slug) => {
    try {
      return await apiRequest(`/blogs/slug/${slug}`);
    } catch (error) {
      console.error(`Error fetching blog post with slug ${slug}:`, error);
      throw error;
    }
  },

  // Create new blog post (admin only)
  createBlogPost: async (blogData) => {
    try {
      return await apiRequest('/blogs', {
        method: 'POST',
        body: JSON.stringify(blogData),
      });
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw error;
    }
  },

  // Update blog post (admin only)
  updateBlogPost: async (id, blogData) => {
    try {
      return await apiRequest(`/blogs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(blogData),
      });
    } catch (error) {
      console.error(`Error updating blog post with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete blog post (admin only)
  deleteBlogPost: async (id) => {
    try {
      return await apiRequest(`/blogs/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error(`Error deleting blog post with ID ${id}:`, error);
      throw error;
    }
  },
};

export default blogAPI;